import styled from "@emotion/styled";
import { UseQueryResult } from "@tanstack/react-query";

import { Info } from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  SpacingToken,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { formatPrice, formatPriceChange } from "helpers/subscription";
import { SubscriptionChangePreviewResponse, WithSubscriptionChange } from "hooks/use-subscription-change-calculation";
import { redirectToSalesTaxHelpCenter } from "routes/subscription/components/SubscriptionCardForm/tax-preview/helpers";
import { IRecurlyCoupon } from "store/entities/recurly";
import { BillingCycle, ISubscription } from "store/entities/subscriptions";
import { withVar } from "themes/helpers";

import { StyledLoader, StyledPricingRow, StyledPricingSummary, StyledRowColumn } from "./styles";

export const billingCyclePriceLabelMap = {
  [BillingCycle.Monthly]: "Monthly total",
  [BillingCycle.Quarterly]: "3mo total",
  [BillingCycle.SemiAnnually]: "6mo total",
  [BillingCycle.Annually]: "Annual total",
  [BillingCycle.TwoYears]: "2yr total",
  [BillingCycle.ThreeYears]: "3yr total",
};

export interface IProps {
  subscription: ISubscription;
  newSubscription: ISubscription;
  coupon?: IRecurlyCoupon | null;
  countryCode?: string;
  zipCode?: string;
  className?: string;
}

export const SubscriptionChangePreview = ({
  subscription,
  newSubscription,
  coupon,
  className,
  countryCode,
  zipCode,
}: IProps): JSX.Element => (
  <WithSubscriptionChange
    current={subscription}
    update={newSubscription}
    couponCode={coupon?.code}
    countryCode={countryCode}
    zipCode={zipCode}
  >
    {(query: UseQueryResult<SubscriptionChangePreviewResponse>) => (
      <StyledPricingSummary className={className}>
        {query.isLoading && <StyledLoader />}
        {query.isFetched &&
          query.data &&
          query.data.result &&
          ((): JSX.Element => {
            const dueNow = query.data.result.dueNow / 100;
            const accountBalance = query.data.result.accountBalance / 100;

            // plan cost
            const planCostGross = query.data.result.planCost / 100;
            const taxValue = query.data.result.taxInCents / 100;
            const planCostNet = planCostGross - taxValue;

            // final price
            const subtotalNet = planCostNet;
            const tax = taxValue;
            const subtotalGros = (subtotalNet + tax).toFixed(2);

            // format prorated charge
            const proportionalChange = parseFloat((dueNow + accountBalance).toFixed(2));
            const formattedPriceChange = formatPriceChange(proportionalChange);

            return (
              <StyledRowColumn alignTop>
                <StyledPricingRow>
                  <Text bold>Subtotal</Text>
                  <Text bold>${subtotalNet.toFixed(2)}</Text>
                </StyledPricingRow>

                {!!tax && (
                  <>
                    <StyledPricingRow>
                      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
                        <Text bold>Tax</Text>
                        <Tooltip placement="top" kind="invert" triggerRenderer={<Icon source={Info} size="small" />}>
                          Tax amount is based on your location.
                          <br />
                          <Button kind="link-inverted" onClick={redirectToSalesTaxHelpCenter}>
                            Read about sales tax
                          </Button>
                        </Tooltip>
                      </Flex>
                      <Text bold>${tax.toFixed(2)}</Text>
                    </StyledPricingRow>

                    <StyledPricingRow>
                      <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
                        <Text bold>{billingCyclePriceLabelMap[newSubscription.billingCycle]}</Text>
                        <Tooltip placement="top" kind="invert" triggerRenderer={<Icon source={Info} size="small" />}>
                          <Button kind="link-inverted" onClick={redirectToSalesTaxHelpCenter}>
                            The cost of your subscription includes sales tax
                          </Button>
                        </Tooltip>
                      </Flex>
                      <Text bold>${subtotalGros}</Text>
                    </StyledPricingRow>
                  </>
                )}

                {proportionalChange !== 0 && (
                  <StyledPricingRow>
                    <div>
                      <StyledText bold>Prorated charge due today</StyledText>
                      <IconWrapper>
                        <Tooltip placement="top" kind="invert" triggerRenderer={<Icon source={Info} size="small" />}>
                          When you change your plan in the middle of your billing cycle, we charge a pro-rated
                          difference between what you’ve already paid and the cost of the new plan for the remaining
                          days in your billing cycle.
                        </Tooltip>
                      </IconWrapper>
                    </div>
                    <Text bold customColor={proportionalChange < 0 ? "green" : undefined}>
                      {formattedPriceChange}
                    </Text>
                  </StyledPricingRow>
                )}

                {proportionalChange >= 0 ? (
                  <StyledPricingRow>
                    <Heading>Billed now</Heading>
                    <Heading>{formatPrice(proportionalChange)}</Heading>
                  </StyledPricingRow>
                ) : (
                  <StyledPricingRow>
                    <Flex alignItems="center" gap={withVar(SpacingToken.Spacing1)}>
                      <Heading>Credit to account</Heading>
                      <Tooltip
                        placement="top"
                        kind="invert"
                        triggerRenderer={
                          <Block marginTop={withVar(SpacingToken.Spacing05)}>
                            <Icon source={Info} size="small" />
                          </Block>
                        }
                      >
                        This credit will be used for your upcoming charges.
                      </Tooltip>
                    </Flex>
                    <Heading size="lg" customColor={withVar(DesignToken.ActionPositiveDefault)}>
                      {formattedPriceChange}
                    </Heading>
                  </StyledPricingRow>
                )}
              </StyledRowColumn>
            );
          })()}
      </StyledPricingSummary>
    )}
  </WithSubscriptionChange>
);

const IconWrapper = styled.div`
  display: inline-block;

  padding-left: ${withVar(SpacingToken.Spacing1)};
  padding-bottom: ${withVar(SpacingToken.Spacing05)};
  vertical-align: bottom;
`;

const StyledText = styled(Text)`
  display: inline;
`;
